import groupBy from "lodash.groupby";
import startCase from "lodash.startcase";
import { ChartColumn } from "lucide-react";
import { DateTime } from "luxon";
import React, { useMemo } from "react";
import { useDispatch } from "react-redux";
import {
  CartesianGrid,
  Label,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";

import {
  IgIconButton,
  IgPaper,
  IgSelect,
  IgTypography,
} from "@intelligems/ig-theme";
import { PluginPerformanceType } from "@intelligems/ig-types";
import { ensureNumber, ensureString } from "@intelligems/ig-utils";
import { Grid, MenuItem, SelectChangeEvent, useTheme } from "@mui/material";
import { lineGroupColors } from "@src/features/analytics/baseDashboard/utils";
import { useGraphValueFormatter } from "@src/features/analytics/hooks/use-graph-value-formatter";
import {
  removePerformanceView,
  setPerformanceView,
} from "@src/store/slices/appSettings";
import { PerformanceView } from "@src/store/types/appSettings";

import { titleFormatter } from "./utils";

interface PerformanceLineChartProps {
  data: PluginPerformanceType[];
  index: number;
  total: number;
  view: PerformanceView;
}

const PerformanceTooltip = ({
  active,
  payload,
  label,
}: TooltipProps<ValueType, NameType>) => {
  if (active && payload && payload.length) {
    if (payload) {
      return (
        <IgPaper
          sx={{
            padding: "10px",
            backgroundColor: "rgba(97, 97, 97, 0.92)",
            border: "1px solid transparent",
            borderRadius: "5px",
          }}
        >
          <Grid container item direction={"column"}>
            <Grid item>
              <IgTypography color={"dark"}>
                <b>Date:</b>{" "}
                {DateTime.fromSeconds(payload[0].payload.completed).toFormat(
                  "yyyy-MM-dd hh:mm"
                )}
              </IgTypography>
            </Grid>
            {payload.map((value) => {
              if (value.dataKey && value.value !== undefined) {
                return (
                  <Grid item key={value.dataKey} sx={{ marginTop: "10px" }}>
                    <IgTypography color={"dark"}>
                      <b>
                        {ensureString(value.dataKey).includes("-blocked")
                          ? "Value without Intelligems"
                          : "Value with Intelligems"}
                        :
                      </b>{" "}
                      {ensureNumber(value.value as number).toFixed(2)}
                    </IgTypography>
                    <Grid item>
                      <IgTypography color={"dark"}>
                        <b>
                          {ensureString(value.dataKey).includes("-blocked")
                            ? "Test without Intelligems"
                            : "Test with Intelligems"}
                          :
                        </b>{" "}
                        {payload[0].payload.id}
                      </IgTypography>
                    </Grid>
                  </Grid>
                );
              }
            })}
          </Grid>
        </IgPaper>
      );
    }
  }

  return null;
};

export const PerformanceLineChart = ({
  data,
  index,
  total,
  view,
}: PerformanceLineChartProps) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const graphValueFormatter = useGraphValueFormatter();

  const chartData = useMemo(() => {
    const groupedByTime = groupBy(data, (_) => _.completed);
    const finalData: PluginPerformanceType[] = [];

    for (const [time, values] of Object.entries(groupedByTime)) {
      const reduced: PluginPerformanceType = values.reduce(
        (prev: PluginPerformanceType, curr: PluginPerformanceType) => {
          return {
            ...prev,
            ...curr,
            results: {
              ...prev.results,
              ...curr.results,
            },
          };
        },
        // @ts-ignore
        {}
      );
      finalData.push(reduced);
    }
    return finalData;
  }, [data]);

  const dropdownKeys = useMemo(() => {
    if (!data.length) {
      return [];
    }

    const keySet = new Set<string>();
    for (const item of data) {
      for (const key of Object.keys(item.results || {})) {
        keySet.add(key.replace("-blocked", ""));
      }
    }
    return Array.from(keySet);
  }, [data]);

  return (
    <IgPaper sx={{ boxShadow: "none" }}>
      {data.length ? (
        <Grid container direction={"row"} sx={{ width: "100%" }} rowSpacing={2}>
          <Grid
            container
            item
            direction={"row"}
            justifyContent={"space-between"}
            flexWrap={"nowrap"}
          >
            <Grid item>
              <IgSelect
                sx={{ backgroundColor: "white" }}
                id={`performance-view-select-${index}`}
                value={view.dataKey || "TBT"}
                onChange={(e: SelectChangeEvent<any>) => {
                  dispatch(
                    setPerformanceView({
                      index,
                      value: { dataKey: e.target.value },
                    })
                  );
                }}
              >
                {dropdownKeys.length ? (
                  dropdownKeys.map((item) => (
                    <MenuItem key={item} value={item}>
                      {titleFormatter(item, true)}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value={"TBT"}>First Paint</MenuItem>
                )}
              </IgSelect>
            </Grid>
            <Grid item justifyContent={"end"}>
              {total > 1 ? (
                <IgIconButton
                  iconType={"close"}
                  iconSize={18}
                  onClick={() => dispatch(removePerformanceView({ index }))}
                />
              ) : null}
            </Grid>
          </Grid>
          <Grid item sx={{ width: "100%" }}>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart
                data={chartData}
                style={{ marginLeft: -24 }}
                margin={{
                  top: 5,
                  bottom: 5,
                }}
              >
                <CartesianGrid
                  style={{ stroke: theme.igBorders.core.disabled }}
                  vertical={false}
                />
                <XAxis
                  // allowDuplicatedCategory={false}
                  label={
                    <Label
                      style={{
                        fontFamily: theme.typography.fontFamily,
                        fontSize: 14,
                      }}
                      value={"Date"}
                      position={"insideBottom"}
                      offset={-5}
                      textAnchor={"middle"}
                    />
                  }
                  padding={{ left: 30, right: 30 }}
                  axisLine={{ stroke: theme.igBorders.core.disabled }}
                  tickLine={false}
                  tickFormatter={(item) =>
                    DateTime.fromSeconds(item).toFormat("yyyy-MM-dd hh:mm")
                  }
                  dataKey={"completed"}
                />
                <YAxis
                  label={
                    <Label
                      style={{
                        fill: theme.palette.text.primary,
                        fontFamily: theme.typography.fontFamily,
                        fontSize: 14,
                        marginTop: "10px",
                        textAnchor: "middle",
                      }}
                      angle={270}
                      value={startCase(view.dataKey)}
                      position={"insideLeft"}
                      offset={10}
                      textAnchor={"middle"}
                    />
                  }
                  style={{
                    fontFamily: theme.typography.fontFamily,
                    fontSize: 14,
                  }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(value: any) =>
                    graphValueFormatter({
                      isAxisTick: true,
                      value,
                      configDataTypes: ["numberNoDecimal"],
                    })!
                  }
                />
                <Tooltip content={<PerformanceTooltip />} />
                <Legend
                  verticalAlign="top"
                  height={36}
                  formatter={(item) =>
                    titleFormatter(item.replace("results.", ""), false).replace(
                      "Blocked",
                      "Without Intelligems"
                    )
                  }
                />
                <Line
                  type="linear"
                  dataKey={`results.${view.dataKey}-blocked`}
                  stroke={lineGroupColors[3]}
                  connectNulls={true}
                />
                <Line
                  type="linear"
                  dataKey={`results.${view.dataKey}`}
                  stroke={lineGroupColors[0]}
                  connectNulls={true}
                />
              </LineChart>
            </ResponsiveContainer>
          </Grid>
        </Grid>
      ) : (
        <Grid
          container
          item
          direction={"column"}
          alignItems={"center"}
          alignContent={"center"}
          justifyContent={"center"}
          textAlign={"center"}
        >
          <Grid item>
            <ChartColumn size={50} />
          </Grid>
          <Grid item>
            <IgTypography>No data yet!</IgTypography>
          </Grid>
          <Grid item>
            <IgTypography>Try running a performance check</IgTypography>
          </Grid>
        </Grid>
      )}
    </IgPaper>
  );
};
